import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { doc, getDoc, collection, getDocs } from 'firebase/firestore'; // Removed setDoc since we're not writing
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { FaCheckCircle } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faCreditCard } from '@fortawesome/free-solid-svg-icons';

function Plans() {
  const [userPlan, setUserPlan] = useState(null);
  const [userCredits, setUserCredits] = useState(0);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [stripePublishableKey, setStripePublishableKey] = useState('');
  const [error, setError] = useState(null);
  const auth = getAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        const userRef = doc(db, 'users', currentUser.uid);
        const stripeRef = doc(db, 'stripeSettings', 'config');
        try {
          const [userSnap, stripeSnap, plansSnapshot] = await Promise.all([
            getDoc(userRef),
            getDoc(stripeRef),
            getDocs(collection(db, 'planSettings')),
          ]);

          if (userSnap.exists()) {
            const userData = userSnap.data();
            setUserPlan(userData.plan || 'basic');
            setUserCredits(userData.credits || 0);
          } else {
            setError('User data not found.');
          }

          if (stripeSnap.exists()) {
            setStripePublishableKey(stripeSnap.data().publishableKey);
          } else {
            console.warn('Stripe config not found.');
          }

          if (plansSnapshot.empty) {
            console.warn('No plans found in planSettings. Using defaults.');
            const defaultPlans = [
              { id: 'basic', name: 'Basic', price: 0, credits: 30, priceId: null },
              { id: 'pro', name: 'Pro', price: 600, credits: 300, priceId: 'price_pro' },
              { id: 'enterprise', name: 'Enterprise', price: 6000, credits: 3000, priceId: 'price_enterprise' },
            ];
            setPlans(defaultPlans.map(transformPlan));
          } else {
            const plansData = plansSnapshot.docs.map((doc) => ({
              id: doc.id,
              ...doc.data(),
            }));
            setPlans(plansData.map(transformPlan));
          }
        } catch (err) {
          console.error('Error fetching data:', err);
          setError('Failed to load plans: ' + err.message);
        }
      } else {
        navigate('/login');
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, [auth, navigate]);

  const transformPlan = (plan) => {
    const credits = plan.credits || 0;
    return {
      name: plan.name || 'Unnamed Plan',
      price: plan.price === 0 ? 'Free' : `$${plan.price}/month`,
      credits: `${credits} credits/month`,
      predictions: `~${Math.floor(credits / 3)}/month (3 credits/prediction)`,
      features: plan.features || getDefaultFeatures(plan.id),
      priceId: plan.priceId || null,
      id: plan.id,
    };
  };

  const getDefaultFeatures = (planId) => {
    switch (planId) {
      case 'basic':
        return ['Limited access', 'Basic predictions'];
      case 'pro':
        return ['Advanced predictions', 'Priority support'];
      case 'enterprise':
        return ['Unlimited predictions', 'Custom features', 'Dedicated support'];
      default:
        return ['Basic access'];
    }
  };

  const handleSelectPlan = async (plan) => {
    console.log('Selected plan:', plan);

    if (plan.name.toLowerCase() === userPlan) {
      alert(`The ${plan.name} plan is already selected.`);
      return;
    }

    if (plan.id === 'basic') {
      alert('The Basic plan is free and requires no payment. Contact support to downgrade.');
      return;
    }

    if (!stripePublishableKey) {
      alert('Stripe configuration is missing. Please contact support.');
      return;
    }

    setPaymentLoading(true);
    try {
      // Get plan credits from plan object
      const credits = parseInt(plan.credits.split(' ')[0]);
      
      // Use the API endpoint with the new path structure
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          priceId: plan.priceId,
          userId: auth.currentUser.uid,
          planId: plan.id,
          credits: credits,
          successUrl: `${window.location.origin}/payment-success`,
          cancelUrl: `${window.location.origin}/plans`,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! Status: ${response.status}`);
      }
      
      const data = await response.json();
      const { sessionId } = data;

      // Load Stripe JS and redirect to checkout
      if (!window.Stripe) {
        const script = document.createElement('script');
        script.src = 'https://js.stripe.com/v3/';
        script.async = true;
        document.body.appendChild(script);
        
        // Wait for script to load
        await new Promise(resolve => {
          script.onload = resolve;
        });
      }
      
      const stripe = window.Stripe(stripePublishableKey);
      const { error } = await stripe.redirectToCheckout({ sessionId });
      
      if (error) {
        throw new Error(error.message);
      }
    } catch (error) {
      console.error('Error redirecting to checkout:', error);
      alert(`Failed to initiate payment: ${error.message}. Please try again.`);
    } finally {
      setPaymentLoading(false);
    }
  };

  const containerVariants = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.1 } } };
  const cardVariants = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } } };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
        <div className="flex items-center space-x-2 text-gray-600">
          <FontAwesomeIcon icon={faSpinner} className="animate-spin text-2xl" />
          <span className="text-lg font-semibold">Loading plans...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
        <div className="bg-red-100 p-6 rounded-lg shadow-md text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <motion.div className="min-h-screen bg-gradient-to-br from-gray-100 to-gray-200 font-['Noto Sans'] py-12 px-4 sm:px-6 lg:px-8" initial="hidden" animate="visible" variants={containerVariants}>
      <div className="max-w-7xl mx-auto">
        <h2 className="text-4xl font-extrabold text-center mb-10 bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-blue-500">
          <FontAwesomeIcon icon={faCreditCard} className="mr-3 text-purple-600" />
          Subscription Plans
        </h2>
        <p className="text-gray-600 mb-6 text-center">
          Your current credits: <strong>{userCredits}</strong>. New users receive <strong>30 free credits</strong> upon registration, renewed every 28 days for Basic plan users!
        </p>
        <motion.div className="grid grid-cols-1 md:grid-cols-3 gap-6" variants={containerVariants}>
          {plans.length === 0 ? (
            <p className="text-gray-600 text-center col-span-3">No plans available at the moment.</p>
          ) : (
            plans.map((plan) => (
              <motion.div key={plan.name} className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200 hover:shadow-xl transition-shadow duration-300" variants={cardVariants} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <h3 className="text-2xl font-semibold text-gray-700 mb-2">{plan.name}</h3>
                <p className="text-gray-600 mb-4">{plan.price}</p>
                <ul className="text-gray-600 space-y-2 mb-6">
                  <li><strong>Credits:</strong> {plan.credits}</li>
                  <li><strong>Predictions:</strong> {plan.predictions}</li>
                  {plan.features.map((feature, index) => <li key={index}>{feature}</li>)}
                </ul>
                <button
                  onClick={() => handleSelectPlan(plan)}
                  disabled={userPlan === plan.name.toLowerCase() || paymentLoading}
                  className={`w-full py-2 rounded-lg text-white font-semibold transition-colors duration-300 ${
                    userPlan === plan.name.toLowerCase()
                      ? 'bg-gray-400 cursor-not-allowed'
                      : paymentLoading
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700'
                  }`}
                >
                  {userPlan === plan.name.toLowerCase() ? (
                    <>
                      <FaCheckCircle className="inline mr-2" /> Selected
                    </>
                  ) : paymentLoading ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                      Processing...
                    </>
                  ) : (
                    'Upgrade'
                  )}
                </button>
              </motion.div>
            ))
          )}
        </motion.div>
        <p className="text-sm text-gray-500 mt-6 text-center italic">
          * Approximate predictions based on an average of 3 credits per prediction. Credits do not expire and can be accumulated.
        </p>
      </div>
    </motion.div>
  );
}

export default Plans;